import React from 'react';
import { Link } from 'react-router-dom';
import { FaPlane, FaHotel, FaUtensils, FaShoppingCart, FaMapMarkedAlt, FaTaxi, FaMoneyBillWave, FaPhone, FaWifi, FaHeart, FaStar, FaCheck, FaPlay, FaBookOpen, FaHeadphones } from 'react-icons/fa';
import SEO from '../components/SEO';

const PersianForTravel: React.FC = () => {
  const essentialPhrases = [
    {
      category: "Greetings & Basic Conversation",
      phrases: [
        { persian: "سلام", english: "Hello", transliteration: "Salaam" },
        { persian: "خداحافظ", english: "Goodbye", transliteration: "Khoda hafez" },
        { persian: "ممنون", english: "Thank you", transliteration: "Mamnoon" },
        { persian: "لطفاً", english: "Please", transliteration: "Lotfan" },
        { persian: "بله", english: "Yes", transliteration: "Bale" },
        { persian: "نه", english: "No", transliteration: "Na" }
      ]
    },
    {
      category: "Transportation",
      phrases: [
        { persian: "تاکسی", english: "Taxi", transliteration: "Taxi" },
        { persian: "اتوبوس", english: "Bus", transliteration: "Otoboos" },
        { persian: "مترو", english: "Metro", transliteration: "Metro" },
        { persian: "کجا می‌رود؟", english: "Where does it go?", transliteration: "Koja miravad?" },
        { persian: "چقدر؟", english: "How much?", transliteration: "Cheghadr?" },
        { persian: "ایستگاه", english: "Station", transliteration: "Istgah" }
      ]
    },
    {
      category: "Food & Dining",
      phrases: [
        { persian: "رستوران", english: "Restaurant", transliteration: "Restaurant" },
        { persian: "کافه", english: "Cafe", transliteration: "Cafe" },
        { persian: "منوی غذا", english: "Menu", transliteration: "Menu-ye ghaza" },
        { persian: "آب", english: "Water", transliteration: "Aab" },
        { persian: "چای", english: "Tea", transliteration: "Chai" },
        { persian: "خوشمزه", english: "Delicious", transliteration: "Khosh mazzeh" }
      ]
    },
    {
      category: "Shopping",
      phrases: [
        { persian: "بازار", english: "Bazaar", transliteration: "Bazaar" },
        { persian: "فروشگاه", english: "Shop", transliteration: "Forooshgah" },
        { persian: "قیمت", english: "Price", transliteration: "Gheymat" },
        { persian: "گران", english: "Expensive", transliteration: "Geraan" },
        { persian: "ارزان", english: "Cheap", transliteration: "Arzaan" },
        { persian: "تخفیف", english: "Discount", transliteration: "Takhfif" }
      ]
    },
    {
      category: "Accommodation",
      phrases: [
        { persian: "هتل", english: "Hotel", transliteration: "Hotel" },
        { persian: "اتاق", english: "Room", transliteration: "Otaagh" },
        { persian: "رزرو", english: "Reservation", transliteration: "Rezerv" },
        { persian: "کلید", english: "Key", transliteration: "Kelid" },
        { persian: "صبحانه", english: "Breakfast", transliteration: "Sobhaaneh" },
        { persian: "اینترنت", english: "Internet", transliteration: "Internet" }
      ]
    }
  ];

  const travelTips = [
    {
      icon: <FaMoneyBillWave className="w-8 h-8 text-green-600" />,
      title: "Currency & Money",
      tips: [
        "Iranian Rial (IRR) is the official currency",
        "Carry cash as many places don't accept cards",
        "Learn to count in Persian for better bargaining",
        "Exchange money at official exchange offices"
      ]
    },
    {
      icon: <FaMapMarkedAlt className="w-8 h-8 text-blue-600" />,
      title: "Navigation & Directions",
      tips: [
        "Download offline maps before your trip",
        "Learn basic direction words in Persian",
        "Ask locals for directions - they're very helpful",
        "Use landmarks when asking for directions"
      ]
    },
    {
      icon: <FaPhone className="w-8 h-8 text-purple-600" />,
      title: "Communication",
      tips: [
        "Get a local SIM card for internet access",
        "Download Persian language apps",
        "Learn emergency numbers (110 for police)",
        "Use translation apps as backup"
      ]
    },
    {
      icon: <FaHeart className="w-8 h-8 text-red-600" />,
      title: "Cultural Etiquette",
      tips: [
        "Greet people with 'Salaam'",
        "Remove shoes when entering homes",
        "Dress modestly, especially women",
        "Accept tea when offered - it's polite"
      ]
    }
  ];

  const popularDestinations = [
    {
      name: "Tehran",
      description: "The bustling capital with modern attractions and historic sites",
      highlights: ["Golestan Palace", "Tajrish Bazaar", "Milad Tower", "Darband Mountain"],
      phrases: ["تهران", "پایتخت", "شلوغ", "مدرن"]
    },
    {
      name: "Isfahan",
      description: "The cultural heart of Iran with stunning Islamic architecture",
      highlights: ["Naqsh-e Jahan Square", "Imam Mosque", "Chehel Sotoun", "Si-o-se Pol Bridge"],
      phrases: ["اصفهان", "زیبا", "تاریخی", "معماری"]
    },
    {
      name: "Shiraz",
      description: "City of poets and gardens, home to Persepolis",
      highlights: ["Persepolis", "Nasir al-Mulk Mosque", "Eram Garden", "Hafez Tomb"],
      phrases: ["شیراز", "شعر", "باغ", "تخت جمشید"]
    },
    {
      name: "Yazd",
      description: "Ancient desert city with unique architecture and culture",
      highlights: ["Jame Mosque", "Towers of Silence", "Amir Chakhmaq Complex", "Windcatchers"],
      phrases: ["یزد", "کویر", "قدیمی", "معماری سنتی"]
    }
  ];

  return (
    <>
      <SEO 
        title="Learn Persian for Travel | Essential Farsi Phrases | پیروز Pirooz"
        description="Learn Persian for travel with essential Farsi phrases, cultural tips, and practical vocabulary. Master Persian language for your trip to Iran and Persian-speaking countries."
        keywords={[
          // Primary Target Keywords
          'learn persian for travel', 'farsi for travel', 'persian travel phrases', 'iran travel language',
          'learn farsi for travel', 'persian for travelers', 'farsi travel language', 'iran travel phrases',
          'persian tourist phrases', 'farsi travel vocabulary', 'learn farsi for iran trip',
          'persian language for travel', 'farsi language for travel', 'iran travel language guide',
          
          // Secondary Variations
          'persian phrases for tourists', 'farsi words for travel', 'iranian language for travelers',
          'persian conversation for travel', 'farsi survival phrases', 'persian travel guide language',
          'learn farsi before iran trip', 'persian language travel course', 'farsi travel course',
          'persian travel vocabulary', 'farsi travel words', 'iran travel vocabulary',
          'persian travel expressions', 'farsi travel expressions', 'iran travel expressions',
          
          // Specific Travel Categories
          'persian restaurant phrases', 'farsi food phrases', 'iranian restaurant language',
          'persian transportation phrases', 'farsi transport phrases', 'iranian transport language',
          'persian shopping phrases', 'farsi shopping phrases', 'iranian shopping language',
          'persian hotel phrases', 'farsi accommodation phrases', 'iranian hotel language',
          'persian greetings travel', 'farsi greetings travel', 'iranian greetings travel',
          
          // Cultural & Contextual Keywords
          'iran travel tips', 'persian culture travel', 'iranian customs for tourists',
          'persian etiquette travel', 'iran travel language guide', 'persian speaking countries travel',
          'iran travel culture', 'persian travel customs', 'iranian travel etiquette',
          'persian travel traditions', 'farsi travel culture', 'iran travel traditions',
          
          // Destination-Specific Keywords
          'tehran language guide', 'isfahan persian phrases', 'shiraz farsi phrases',
          'yazd persian language', 'iran cities language', 'persian cities travel',
          'tehran travel phrases', 'isfahan travel language', 'shiraz travel phrases',
          'yazd travel language', 'iran travel destinations', 'persian travel destinations',
          
          // Practical Travel Keywords
          'persian emergency phrases', 'farsi emergency phrases', 'iran emergency language',
          'persian numbers travel', 'farsi numbers travel', 'iranian numbers travel',
          'persian directions travel', 'farsi directions travel', 'iranian directions travel',
          'persian money phrases', 'farsi money phrases', 'iranian money language',
          
          // Learning Method Keywords
          'persian travel audio', 'farsi travel audio', 'iran travel audio lessons',
          'persian travel flashcards', 'farsi travel flashcards', 'iran travel flashcards',
          'persian travel app', 'farsi travel app', 'iran travel language app',
          'persian travel practice', 'farsi travel practice', 'iran travel practice',
          
          // Competitor Keywords
          'persian travel immersion', 'farsi travel immersion', 'iran travel immersion',
          'persian travel conversation', 'farsi travel conversation', 'iran travel conversation',
          'persian travel speaking', 'farsi travel speaking', 'iran travel speaking',
          'persian travel listening', 'farsi travel listening', 'iran travel listening',
          
          // Related Keywords
          'persian speaking countries', 'farsi speaking countries', 'iranian language countries',
          'persian language map', 'farsi language map', 'iranian language map',
          'persian language regions', 'farsi language regions', 'iranian language regions',
          'persian diaspora', 'farsi diaspora', 'iranian diaspora'
        ]}
        canonicalUrl="/persian-for-travel"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Course",
          "name": "Persian for Travel - Essential Farsi Phrases Course",
          "description": "Learn essential Persian phrases and cultural tips for traveling in Iran and Persian-speaking countries",
          "provider": {
            "@type": "EducationalOrganization",
            "name": "پیروز Pirooz"
          },
          "educationalLevel": "Beginner",
          "inLanguage": ["en", "fa"],
          "courseCode": "FARSI-TRAVEL",
          "educationalCredentialAwarded": "Travel Persian Certificate",
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "inLanguage": ["en", "fa"],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            }
          }
        }}
      />
      
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-green-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Learn Persian for Travel
                <span className="block text-2xl md:text-3xl mt-2 text-green-200">
                  Essential Farsi Phrases for Your Journey
                </span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
                Master essential Persian phrases and cultural tips to enhance your travel experience in Iran and Persian-speaking countries
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/pathways" 
                  className="px-8 py-4 bg-white text-green-600 font-bold rounded-lg hover:bg-gray-100 transition duration-300 text-lg"
                >
                  Start Learning
                </Link>
                <Link 
                  to="/travel-companion" 
                  className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-green-600 transition duration-300 text-lg"
                >
                  Travel Companion
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Essential Phrases Section */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Essential Persian Phrases for Travel
              </h2>
              <p className="text-xl text-gray-600">
                Master these key phrases to navigate, communicate, and connect with locals during your travels
              </p>
            </div>
            
            <div className="space-y-12">
              {essentialPhrases.map((category, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <FaBookOpen className="w-6 h-6 text-blue-600 mr-3" />
                    {category.category}
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {category.phrases.map((phrase, phraseIndex) => (
                      <div key={phraseIndex} className="bg-white rounded-lg p-6 shadow-md">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900 mb-2" dir="rtl">
                            {phrase.persian}
                          </div>
                          <div className="text-lg text-blue-600 mb-2">
                            {phrase.english}
                          </div>
                          <div className="text-sm text-gray-500 italic">
                            {phrase.transliteration}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Travel Tips Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Travel Tips & Cultural Insights
              </h2>
              <p className="text-xl text-gray-600">
                Essential advice to make your Persian-speaking country travels smooth and enjoyable
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {travelTips.map((tip, index) => (
                <div key={index} className="bg-white rounded-lg p-8 shadow-lg">
                  <div className="flex items-center mb-4">
                    {tip.icon}
                    <h3 className="text-xl font-semibold ml-3">{tip.title}</h3>
                  </div>
                  <ul className="space-y-3">
                    {tip.tips.map((tipItem, tipIndex) => (
                      <li key={tipIndex} className="flex items-start text-gray-700">
                        <FaCheck className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                        <span>{tipItem}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Popular Destinations */}
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Popular Persian-Speaking Destinations
              </h2>
              <p className="text-xl text-gray-600">
                Discover key phrases and insights for major Persian-speaking cities
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {popularDestinations.map((destination, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{destination.name}</h3>
                  <p className="text-gray-600 mb-4">{destination.description}</p>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold mb-3 text-gray-800">Highlights</h4>
                    <ul className="space-y-2">
                      {destination.highlights.map((highlight, highlightIndex) => (
                        <li key={highlightIndex} className="flex items-center text-gray-700">
                          <FaStar className="w-4 h-4 text-yellow-500 mr-2" />
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="text-lg font-semibold mb-3 text-gray-800">Key Phrases</h4>
                    <div className="flex flex-wrap gap-2">
                      {destination.phrases.map((phrase, phraseIndex) => (
                        <span key={phraseIndex} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium" dir="rtl">
                          {phrase}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Interactive Learning Section */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Practice Your Travel Persian
              </h2>
              <p className="text-xl text-gray-600">
                Interactive exercises to help you master essential travel phrases
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-lg p-8 shadow-lg text-center">
                <FaHeadphones className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">Audio Practice</h3>
                <p className="text-gray-600 mb-6">
                  Listen and repeat essential phrases with native pronunciation
                </p>
                <Link 
                  to="/pathways" 
                  className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition duration-300"
                >
                  Start Listening
                </Link>
              </div>
              
              <div className="bg-white rounded-lg p-8 shadow-lg text-center">
                <FaPlay className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">Conversation Practice</h3>
                <p className="text-gray-600 mb-6">
                  Practice real travel scenarios with interactive dialogues
                </p>
                <Link 
                  to="/forum" 
                  className="inline-block px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition duration-300"
                >
                  Join Conversations
                </Link>
              </div>
              
              <div className="bg-white rounded-lg p-8 shadow-lg text-center">
                <FaBookOpen className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-4">Cultural Guide</h3>
                <p className="text-gray-600 mb-6">
                  Learn about Persian customs, etiquette, and cultural insights
                </p>
                <Link 
                  to="/cultural" 
                  className="inline-block px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition duration-300"
                >
                  Explore Culture
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="py-16 bg-green-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Learn Persian for Your Travels?
            </h2>
            <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
              Start learning essential Persian phrases today and enhance your travel experience in Persian-speaking countries
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register" 
                className="px-8 py-4 bg-white text-green-600 font-bold rounded-lg hover:bg-gray-100 transition duration-300 text-lg"
              >
                Start Learning Free
              </Link>
              <Link 
                to="/travel-companion" 
                className="px-8 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white hover:text-green-600 transition duration-300 text-lg"
              >
                Get Travel Companion
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersianForTravel;
